import PropTypes from 'prop-types';
import { useState } from 'react';
import { Button, Stack, Tooltip, Typography, LinearProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

import Label from '../../../components/label';
import { ConfirmationDialog } from '../../../components';
import { deleteAuditRecord } from '../store/auditActions';
import { fDate } from '../../../utils/formatTime';

const STATUS_COLOUR = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'default',
};

const readableStatus = (status) => (status || '').replace(/_/g, ' ').toLowerCase();

AuditTableRow.propTypes = {
  row: PropTypes.object,
  canManage: PropTypes.bool,
  onRefresh: PropTypes.func,
};

export default function AuditTableRow({ row, canManage, onRefresh }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    const result = await dispatch(deleteAuditRecord(row.id));
    setIsDeleting(false);
    if (result.ok) {
      setConfirmOpen(false);
      if (onRefresh) onRefresh();
    }
  };

  const scanned = row?.total_scanned ?? 0;
  const total = row?.total_assets ?? 0;
  const misplaced = row?.total_misplaced ?? 0;
  const missing = row?.total_missing ?? 0;

  return (
    <>
      <TableRow hover tabIndex={-1}>
        <TableCell align="left">{row?.name}</TableCell>
        <TableCell align="left">{row?.location?.name}</TableCell>
        <TableCell align="left" sx={{ minWidth: 140 }}>
          <Typography variant="caption">
            {scanned}/{total}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(row?.progress ?? 0, 100)}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </TableCell>
        <TableCell align="left">
          {/* The two findings a stock take exists to produce. The old screen
              could show neither, because nothing recorded what was expected. */}
          <Stack direction="row" spacing={0.5}>
            {missing > 0 && (
              <Tooltip title="On the list, never scanned">
                <Box component="span">
                  <Label color="error">{missing} missing</Label>
                </Box>
              </Tooltip>
            )}
            {misplaced > 0 && (
              <Tooltip title="Scanned here, but the register files them elsewhere">
                <Box component="span">
                  <Label color="warning">{misplaced} misplaced</Label>
                </Box>
              </Tooltip>
            )}
            {missing === 0 && misplaced === 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                —
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell align="left">{fDate(row?.start_date)}</TableCell>
        <TableCell align="left">{fDate(row?.end_date)}</TableCell>
        <TableCell align="left">{row?.auditor?.user?.display_name}</TableCell>
        <TableCell align="left">{fDate(row?.completed_date)}</TableCell>
        <TableCell align="left">
          <Label color={STATUS_COLOUR[row?.status] || 'default'}>{readableStatus(row?.status)}</Label>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" variant="contained" onClick={() => navigate(`/dashboard/audits/${row?.id}`)}>
              {row?.audit_completed ? 'View' : 'Count'}
            </Button>
            {/* A completed stock take is the evidence of what was found, so the
                API refuses to delete one. Don't offer the button either. */}
            {canManage && !row?.audit_completed && (
              <Button size="small" variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
                Delete
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      <ConfirmationDialog
        openDialog={confirmOpen}
        handleDialog={() => setConfirmOpen(false)}
        title="Delete this stock take?"
        description={`"${row?.name}" and its checklist will be hidden. Assets are not affected.`}
        confirmLabel="Delete"
        loading={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  );
}
